import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useTheme } from '../ThemeContext'
import { useLayout } from '../LayoutContext'
import { useAuth } from '../AuthContext'
import { useOrganization } from '../hooks/useOrganization'
import { FileText, MessageSquareWarning } from 'lucide-react'

const STATS_CACHE_KEY = 'dashboard_stats_cache'
const MY_INSPECTIONS_CACHE_KEY = 'dashboard_my_inspections_cache'
const MY_COMPLAINTS_CACHE_KEY = 'dashboard_my_complaints_cache'

export default function Dashboard({ onStatusFilterClick, agentFilter }) {
  const { colors, spacing, typography, darkMode } = useTheme()
  const { isModernLayout } = useLayout()
  const { user } = useAuth()
  const { organization } = useOrganization()

  // Restore stats from cache for instant display
  const [stats, setStats] = useState(() => {
    try {
      const cached = localStorage.getItem(STATS_CACHE_KEY)
      if (cached) {
        const parsed = JSON.parse(cached)
        // Only use cache if it's for the current organization AND agent filter
        if (parsed.orgId === organization?.id && parsed.agentFilter === agentFilter) {
          return parsed.stats
        }
      }
    } catch (e) {
      console.error('Error loading cached stats:', e)
    }
    return {
      total: 0,
      current: 0,
      dueSoon: 0,
      overdue: 0,
      never: 0
    }
  })

  const [myInspectionsThisMonth, setMyInspectionsThisMonth] = useState(() => {
    try {
      const cached = localStorage.getItem(MY_INSPECTIONS_CACHE_KEY)
      return cached ? parseInt(cached, 10) : 0
    } catch {
      return 0
    }
  })

  const [myOpenComplaints, setMyOpenComplaints] = useState(() => {
    try {
      const cached = localStorage.getItem(MY_COMPLAINTS_CACHE_KEY)
      return cached ? parseInt(cached, 10) : 0
    } catch {
      return 0
    }
  })

  const [loading, setLoading] = useState(false) // Start with false since we have cached data

  // Fetch stats on mount and when agentFilter or organization changes
  useEffect(() => {
    // Don't fetch until organization is loaded (prevents fetching all data)
    if (!organization) return

    // Set loading state when filter changes
    setLoading(true)
    fetchStats()
    fetchMyInspectionsThisMonth()
    fetchMyOpenComplaints()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter, organization])

  // Poll every 60 seconds instead of holding persistent realtime connections
  useEffect(() => {
    if (!organization) return
    const interval = setInterval(() => {
      fetchStats()
      fetchMyInspectionsThisMonth()
      fetchMyOpenComplaints()
    }, 60000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, agentFilter])

  async function fetchStats() {
    try {
      // Step 1: Fetch establishments (no inline inspections — eliminates expensive lateral join)
      const allEstablishments = []
      const batchSize = 1000
      let hasMore = true
      let offset = 0

      while (hasMore) {
        let query = supabase
          .from('establishments')
          .select('id, risk_level, users:assigned_inspector_id(first_name, last_name)')
          .eq('active', true)
          .eq('establishment_type', 'mxb')

        if (organization?.id) query = query.eq('organization_id', organization.id)

        const { data, error } = await query.range(offset, offset + batchSize - 1)
        if (error) throw error
        if (data && data.length > 0) {
          allEstablishments.push(...data)
          offset += batchSize
          hasMore = data.length === batchSize
        } else {
          hasMore = false
        }
      }

      // Step 2: Filter by agent if selected
      let establishments = allEstablishments
      if (agentFilter && agentFilter.trim() !== '') {
        establishments = allEstablishments.filter(est => {
          if (!est.users) return false
          return `${est.users.first_name} ${est.users.last_name}` === agentFilter
        })
      }

      // Step 3: Fetch last inspection dates via RPC (single query, same pattern as useEstablishments)
      const estIds = establishments.map(e => e.id)
      const lastInspectionMap = {}
      for (let i = 0; i < estIds.length; i += 500) {
        const { data: lastDates } = await supabase
          .rpc('get_last_inspection_dates', { est_ids: estIds.slice(i, i + 500) })
        if (lastDates) {
          for (const row of lastDates) lastInspectionMap[row.establishment_id] = row.inspection_date
        }
      }

      // Step 4: Compute status stats
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const calculatedStats = { total: establishments.length, current: 0, dueSoon: 0, overdue: 0, never: 0 }

      establishments.forEach(est => {
        const lastDateStr = lastInspectionMap[est.id]
        if (!lastDateStr) { calculatedStats.never++; return }

        const [year, month, day] = lastDateStr.split('-').map(Number)
        const daysSince = Math.floor((today - new Date(year, month - 1, day)) / (1000 * 60 * 60 * 24))

        if (est.risk_level === 'high') {
          if (daysSince <= 60) calculatedStats.current++
          else if (daysSince <= 90) calculatedStats.dueSoon++
          else calculatedStats.overdue++
        } else if (est.risk_level === 'medium') {
          if (daysSince <= 120) calculatedStats.current++
          else if (daysSince <= 180) calculatedStats.dueSoon++
          else calculatedStats.overdue++
        } else {
          if (daysSince <= 180) calculatedStats.current++
          else if (daysSince <= 365) calculatedStats.dueSoon++
          else calculatedStats.overdue++
        }
      })

      setStats(calculatedStats)

      try {
        localStorage.setItem(STATS_CACHE_KEY, JSON.stringify({
          stats: calculatedStats,
          orgId: organization?.id,
          agentFilter: agentFilter || '',
          timestamp: Date.now()
        }))
      } catch (e) {
        console.error('Error caching stats:', e)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  async function fetchMyInspectionsThisMonth() {
    if (!user) return

    try {
      // Get the first and last day of the current month
      const now = new Date()
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)

      // Query inspections for this user in the current month
      let query = supabase
        .from('inspections')
        .select('id')
        .eq('inspector_id', user.id)
        .gte('inspection_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('inspection_date', lastDayOfMonth.toISOString().split('T')[0])

      // Filter by organization if provided (important for super_admins)
      if (organization?.id) {
        query = query.eq('organization_id', organization.id)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching inspections:', error)
        return
      }

      const count = data?.length || 0
      setMyInspectionsThisMonth(count)

      // Cache the count
      try {
        localStorage.setItem(MY_INSPECTIONS_CACHE_KEY, count.toString())
      } catch (e) {
        console.error('Error caching my inspections count:', e)
      }
    } catch (error) {
      console.error('Error in fetchMyInspectionsThisMonth:', error)
    }
  }

  async function fetchMyOpenComplaints() {
    if (!user) return

    try {
      // Query open complaints assigned to this user
      let query = supabase
        .from('complaints')
        .select('id')
        .eq('assigned_to_id', user.id)
        .in('status', ['new', 'in_progress']) // Only count open complaints

      // Filter by organization if provided
      if (organization?.id) {
        query = query.eq('organization_id', organization.id)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching complaints:', error)
        return
      }

      const count = data?.length || 0
      setMyOpenComplaints(count)

      // Cache the count
      try {
        localStorage.setItem(MY_COMPLAINTS_CACHE_KEY, count.toString())
      } catch (e) {
        console.error('Error caching complaints count:', e)
      }
    } catch (error) {
      console.error('Error in fetchMyOpenComplaints:', error)
    }
  }


  const StatPill = ({ title, value, color, onClick }) => {
    const pillColors = {
      '#6b7280': { bg: 'rgba(90,104,120,0.1)',  text: '#4a6080' },
      '#10b981': { bg: 'rgba(42,122,80,0.1)',   text: '#2a7a50' },
      '#f59e0b': { bg: 'rgba(138,98,0,0.1)',    text: '#8a6200' },
      '#ef4444': { bg: 'rgba(138,42,42,0.1)',   text: '#8a2a2a' },
    }
    const modern = isModernLayout && pillColors[color]
    return (
      <button
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          backgroundColor: modern ? modern.bg : colors.bg.primary,
          border: modern ? 'none' : `1px solid ${colors.border.primary}`,
          borderRadius: '9999px',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => { if (onClick) e.currentTarget.style.filter = 'brightness(0.94)' }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = 'none' }}
      >
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: modern ? modern.text : color, flexShrink: 0 }} />
        <span style={{ fontSize: typography.fontSizes.sm, color: modern ? modern.text : colors.text.secondary, fontWeight: typography.fontWeights.medium }}>{title}</span>
        <span style={{ fontSize: typography.fontSizes.base, color: modern ? modern.text : colors.text.primary, fontWeight: typography.fontWeights.bold }}>{value}</span>
      </button>
    )
  }

  if (isModernLayout) {
    const dividerColor = darkMode ? 'rgba(90,127,173,0.18)' : 'rgba(61,100,148,0.12)'
    const kpiPanels = [
      { label: 'Total',    value: stats.total,   color: darkMode ? '#91a9c2' : '#506b87', filter: '' },
      { label: 'Current',  value: stats.current, color: darkMode ? '#37d67a' : '#10884a', filter: 'current' },
      { label: 'Due Soon', value: stats.dueSoon, color: darkMode ? '#ffbd4a' : '#b46904', filter: 'due_soon' },
      { label: 'Overdue',  value: stats.overdue, color: darkMode ? '#ff5f6d' : '#c9283c', filter: 'overdue' },
    ]


    return (
      <div className="dashboard-container" style={{
        margin: '1rem 1.25rem 0',
        backgroundColor: colors.bg.card,
        border: `1px solid ${colors.border.primary}`,
        borderRadius: '8px',
        boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.28)' : '0 4px 24px rgba(31,66,104,0.08)',
        display: 'flex',
        overflow: 'hidden',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      }}>
        <style>{`@media (max-width: 768px) { .dashboard-container { display: none !important; } }`}</style>

        {loading && stats.total === 0 ? (
          <span style={{ padding: '16px', fontSize: '12px', color: colors.text.tertiary }}>Loading…</span>
        ) : kpiPanels.map((panel, i) => (
          <button
            key={panel.label}
            onClick={() => onStatusFilterClick && onStatusFilterClick(panel.filter)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '14px 20px 12px',
              background: 'none',
              border: 'none',
              borderLeft: i > 0 ? `1px solid ${dividerColor}` : 'none',
              cursor: 'pointer',
              transition: 'background 0.15s ease',
              fontFamily: 'inherit',
              textAlign: 'left'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
          >
            <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: panel.color, opacity: 0.85, marginBottom: '4px' }}>{panel.label}</span>
            <span style={{ fontSize: '28px', fontWeight: '700', lineHeight: 1, letterSpacing: '-0.03em', color: panel.color }}>{panel.value}</span>
          </button>
        ))}

        {/* My inspections panel */}
        <button style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '14px 20px 12px',
          background: 'none',
          border: 'none',
          borderLeft: `1px solid ${dividerColor}`,
          cursor: 'default',
          fontFamily: 'inherit',
          textAlign: 'left'
        }}>
          <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.text.tertiary, marginBottom: '4px' }}>My Month</span>
          <span style={{ fontSize: '28px', fontWeight: '700', lineHeight: 1, letterSpacing: '-0.03em', color: darkMode ? '#6da8ff' : '#216fd2' }}>{myInspectionsThisMonth}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="dashboard-container" style={{
      padding: spacing.md,
      backgroundColor: colors.bg.secondary,
      borderBottom: `1px solid ${colors.border.primary}`,
    }}>
      <style>{`@media (max-width: 768px) { .dashboard-container { display: none !important; } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm }}>
        {/* Stat Pills - Left */}
        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          {loading && stats.total === 0 ? (
            <span style={{ color: colors.text.tertiary, fontSize: typography.fontSizes.sm }}>Loading stats...</span>
          ) : (
            <>
              <StatPill title="Total"    value={stats.total}   color="#6b7280" onClick={() => onStatusFilterClick && onStatusFilterClick('')} />
              <StatPill title="Current"  value={stats.current} color="#10b981" onClick={() => onStatusFilterClick && onStatusFilterClick('current')} />
              <StatPill title="Due Soon" value={stats.dueSoon} color="#f59e0b" onClick={() => onStatusFilterClick && onStatusFilterClick('due_soon')} />
              <StatPill title="Overdue"  value={stats.overdue} color="#ef4444" onClick={() => onStatusFilterClick && onStatusFilterClick('overdue')} />
            </>
          )}
        </div>
        {/* Info Pills - Right */}
        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          {agentFilter && agentFilter.trim() !== '' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.sm} ${spacing.md}`, backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.primary}`, borderRadius: '9999px', fontSize: typography.fontSizes.sm, color: colors.text.secondary }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3b82f6', flexShrink: 0 }} />
              Filtered: <strong style={{ color: colors.text.primary }}>{agentFilter}</strong>
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.sm} ${spacing.md}`, backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.primary}`, borderRadius: '9999px', fontSize: typography.fontSizes.sm, color: colors.text.secondary }}>
            <FileText size={14} style={{ color: '#3b82f6' }} />
            My inspections this month: <strong style={{ color: colors.text.primary }}>{myInspectionsThisMonth}</strong>
          </span>
          {myOpenComplaints > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.sm} ${spacing.md}`, backgroundColor: colors.bg.primary, border: `1px solid ${colors.border.primary}`, borderRadius: '9999px', fontSize: typography.fontSizes.sm, color: colors.text.secondary }}>
              <MessageSquareWarning size={14} style={{ color: '#f59e0b' }} />
              Open complaints: <strong style={{ color: colors.text.primary }}>{myOpenComplaints}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}